import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, CheckCircle2 } from "lucide-react";
import PublicNav from "./PublicNav";
import PublicFooter from "./PublicFooter";

/**
 * Shell partagée pour les pages publiques d'audience (Talents, Employeurs,
 * Investisseurs, Universités). Chaque page fournit sa configuration (hero,
 * services, étapes, "pourquoi", CTA) — la structure et le design restent
 * strictement cohérents avec les pages publiques existantes (Solutions,
 * Ecosystems, Pricing).
 */
export default function AudiencePageShell({
  testIdRoot,          // ex: "afr-audience-talents-root"
  breadcrumb,          // { label: "Talents" }
  eyebrow,             // ex: "Pour les talents"
  heroTitle,           // JSX ou string du titre H1
  heroText,            // paragraphe hero
  ctaPrimary,          // { label, to }
  ctaSecondary,        // { label, to }
  servicesTitle,       // ex: "Les solutions pour les talents"
  servicesLead,        // texte descriptif
  services,            // [{ icon: Component, title, desc, color }]
  stepsTitle,          // "Comment ça fonctionne"
  steps,               // [string]  — étapes en cascade
  whyTitle,            // "Pourquoi AFRICTAL ?"
  whyText,             // paragraphe pourquoi
  ctaFinalTitle,       // titre de la bannière finale
  ctaFinalPrimary,     // { label, to, icon: Component? }
  ctaFinalSecondary,   // { label, to }
  accentColor = "var(--afr-primary)",
}) {
  return (
    <div className="afr-root" data-testid={testIdRoot}>
      <PublicNav />

      <div style={{ padding: "1.2rem 2rem 0" }}>
        <nav className="afr-breadcrumb" aria-label="breadcrumb" data-testid={`${testIdRoot}-breadcrumb`}>
          <Link to="/" data-testid={`${testIdRoot}-crumb-home`}>Accueil</Link>
          <span className="afr-breadcrumb__sep">/</span>
          <span className="afr-breadcrumb__current">{breadcrumb.label}</span>
        </nav>
      </div>

      {/* HERO */}
      <header className="afr-hero afr-hero--split" data-testid={`${testIdRoot}-hero`}>
        <div className="afr-hero__grid">
          <div className="afr-hero__left">
            <span className="afr-eyebrow" style={{ color: accentColor }}>{eyebrow}</span>
            <h1 className="afr-hero__title" style={{ marginTop: "0.4rem" }}>{heroTitle}</h1>
            <p className="afr-hero__lead">{heroText}</p>
            <div className="afr-hero__cta-row" style={{ marginTop: "1.6rem", display: "flex", gap: "0.8rem", flexWrap: "wrap" }}>
              <Link to={ctaPrimary.to} className="afr-btn afr-btn--primary afr-btn--lg" data-testid={`${testIdRoot}-hero-cta-primary`}>
                {ctaPrimary.icon ? <ctaPrimary.icon size={16} /> : null} {ctaPrimary.label}
              </Link>
              <Link to={ctaSecondary.to} className="afr-btn afr-btn--outline afr-btn--lg" data-testid={`${testIdRoot}-hero-cta-secondary`}>
                {ctaSecondary.label} <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
          <div className="afr-hero__right" aria-hidden="true">
            <div className="afr-hero-scene" style={{ minHeight: 320 }}>
              <svg viewBox="0 0 480 380" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
                <defs>
                  <radialGradient id={`${testIdRoot}-glow`} cx="50%" cy="45%" r="60%">
                    <stop offset="0%" stopColor="rgba(0,150,64,0.16)" />
                    <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                  </radialGradient>
                </defs>
                <ellipse cx="240" cy="190" rx="220" ry="150" fill={`url(#${testIdRoot}-glow)`} />
                {[[120,110],[360,110],[120,270],[360,270]].map(([cx,cy],i) => (
                  <g key={i}>
                    <circle cx={cx} cy={cy} r="30" fill="rgba(0,150,64,0.10)" />
                    <circle cx={cx} cy={cy} r="18" fill={accentColor} opacity="0.85" />
                  </g>
                ))}
                <line x1="120" y1="110" x2="240" y2="190" stroke="rgba(0,150,64,0.35)" strokeDasharray="4 4" />
                <line x1="360" y1="110" x2="240" y2="190" stroke="rgba(0,150,64,0.35)" strokeDasharray="4 4" />
                <line x1="120" y1="270" x2="240" y2="190" stroke="rgba(0,150,64,0.35)" strokeDasharray="4 4" />
                <line x1="360" y1="270" x2="240" y2="190" stroke="rgba(0,150,64,0.35)" strokeDasharray="4 4" />
                <circle cx="240" cy="190" r="44" fill="var(--afr-secondary)" />
                <text x="240" y="194" textAnchor="middle" fill="#F7F5F0" fontSize="12" fontFamily="Outfit" fontWeight="800">AFRICTAL</text>
              </svg>
            </div>
          </div>
        </div>
      </header>

      {/* SERVICES */}
      <section className="afr-section" data-testid={`${testIdRoot}-services`}>
        <h2 className="afr-section__title">{servicesTitle}</h2>
        {servicesLead && <p className="afr-section__lead">{servicesLead}</p>}
        <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "1.1rem", marginTop: "1.2rem" }}>
          {services.map((s, i) => {
            const Icon = s.icon;
            const color = s.color || accentColor;
            return (
              <article
                key={i}
                className="afr-card"
                data-testid={`${testIdRoot}-service-${i}`}
                style={{ borderTop: `3px solid ${color}` }}
              >
                <div className="afr-persona__icon" style={{ background: `linear-gradient(135deg, ${color}, var(--afr-accent))`, marginBottom: "0.8rem" }}>
                  <Icon size={22} />
                </div>
                <h3 style={{ fontFamily: "'Outfit',sans-serif", fontSize: "1.1rem", margin: "0 0 0.4rem" }}>{s.title}</h3>
                <p className="afr-muted" style={{ margin: 0, lineHeight: 1.55 }}>{s.desc}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* COMMENT ÇA FONCTIONNE */}
      <section className="afr-section" data-testid={`${testIdRoot}-steps`}>
        <h2 className="afr-section__title">{stepsTitle}</h2>
        <div
          className="afr-card"
          style={{
            padding: "2rem",
            background: "linear-gradient(135deg, color-mix(in oklab, var(--afr-primary) 6%, var(--afr-surface)), var(--afr-surface))",
            borderColor: "color-mix(in oklab, var(--afr-primary) 20%, var(--afr-border))",
            marginTop: "1rem",
          }}
        >
          <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {steps.map((step, i) => (
              <li
                key={i}
                data-testid={`${testIdRoot}-step-${i}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  padding: "0.9rem 1rem",
                  background: "var(--afr-surface)",
                  borderRadius: 12,
                  border: `1px solid color-mix(in oklab, ${accentColor} 20%, var(--afr-border))`,
                }}
              >
                <div
                  style={{
                    width: 36, height: 36, minWidth: 36, borderRadius: "50%",
                    background: `linear-gradient(135deg, ${accentColor}, var(--afr-accent))`,
                    color: "#fff", display: "grid", placeItems: "center",
                    fontFamily: "'Outfit',sans-serif", fontWeight: 700, fontSize: "0.95rem",
                  }}
                >
                  {i + 1}
                </div>
                <span style={{ fontFamily: "'Outfit',sans-serif", color: "var(--afr-secondary)", fontSize: "1rem" }}>
                  {step}
                </span>
                {i < steps.length - 1 && (
                  <ArrowRight size={16} style={{ marginLeft: "auto", color: "var(--afr-muted)" }} aria-hidden="true" />
                )}
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* POURQUOI AFRICTAL ? */}
      <section className="afr-section" data-testid={`${testIdRoot}-why`}>
        <div
          className="afr-card"
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            gap: "1.6rem",
            padding: "2rem",
            background: "var(--afr-surface)",
            borderRadius: 20,
          }}
        >
          <div style={{
            width: 64, height: 64, borderRadius: 16, background: `linear-gradient(135deg, ${accentColor}, var(--afr-accent))`,
            display: "grid", placeItems: "center", color: "#fff",
          }}>
            <CheckCircle2 size={28} />
          </div>
          <div>
            <span className="afr-eyebrow" style={{ color: accentColor }}>Notre différence</span>
            <h2 style={{ fontFamily: "'Outfit',sans-serif", fontSize: "1.6rem", margin: "0.3rem 0 0.6rem", lineHeight: 1.25 }}>
              {whyTitle}
            </h2>
            <p style={{ lineHeight: 1.7, margin: 0, color: "var(--afr-secondary)" }}>
              {whyText}
            </p>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="afr-section" data-testid={`${testIdRoot}-cta-final`}>
        <div className="afr-cta-banner">
          <div className="afr-cta-banner__text">
            <h2>{ctaFinalTitle}</h2>
          </div>
          <div className="afr-cta-banner__actions">
            <Link to={ctaFinalPrimary.to} className="afr-btn afr-btn--primary afr-btn--lg" data-testid={`${testIdRoot}-final-cta-primary`}>
              {ctaFinalPrimary.icon ? <ctaFinalPrimary.icon size={16} /> : null} {ctaFinalPrimary.label}
            </Link>
            <Link
              to={ctaFinalSecondary.to}
              className="afr-btn afr-btn--outline afr-btn--lg"
              style={{ color: "#F7F5F0", borderColor: "rgba(247,245,240,0.3)" }}
              data-testid={`${testIdRoot}-final-cta-secondary`}
            >
              {ctaFinalSecondary.label} <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
